import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../Store/store';
import { GameId, GAME_METADATAS, GameMetadata, LaunchStatus } from '@mental-mint/shared-types';
import { shouldShowInMoreGames, shouldShowGameDescription } from '@mental-mint/shared-utils';
import { tileClickerIconLarge, witocracyIconLarge, wordDriftIconLarge, mentalMintLogo } from '../Assets/GameIcons';

interface MoreGamesProps {
  includeLandingPageLink?: boolean;
}

const MoreGames: React.FC<MoreGamesProps> = ({ includeLandingPageLink = false }) => {
    const { currentGame } = useSelector((state: RootState) => state.gameSession);
    const textColor = currentGame?.colorSecondary || 'rgba(255, 255, 255, 0.5)';

    // Map GameId to high-quality bundled game icons (falls back to manifest.gameIcon for unmapped games)
    const gameIconMap: Partial<Record<GameId, string>> = {
        [GameId.TileClicker]: tileClickerIconLarge,
        [GameId.BanterBattle]: witocracyIconLarge,
        [GameId.Taptionary]: wordDriftIconLarge,
    };

    // Sort games by launch status: RELEASED first, ANNOUNCED second, UNANNOUNCED last
    const getSortOrder = (launchStatus: LaunchStatus): number => {
        switch (launchStatus) {
            case LaunchStatus.RELEASED:
                return 0; // First
            case LaunchStatus.ANNOUNCED:
                return 1; // Second
            case LaunchStatus.UNANNOUNCED:
                return 2; // Last
            default:
                return 3;
        }
    };

    const renderSingleGame = (manifest: GameMetadata) => {
        // Determine what text to show: description or "coming soon"
        const showDescription = shouldShowGameDescription(manifest.launchStatus);
        const displayText = showDescription 
            ? manifest.gameDescription 
            : "Coming soon";
        
        // Build URL with UTM parameters for tracking
        const buildGameUrlWithTracking = (): string => {
            const baseUrl = manifest.gameURL;
            const utmSource = currentGame?.metadata?.gameId || 'landing-page';
            const utmMedium = currentGame?.metadata?.gameId ? 'more-modal' : 'more-section';
            
            // Add UTM parameters to track cross-game navigation
            const url = new URL(baseUrl);
            url.searchParams.set('utm_source', utmSource);
            url.searchParams.set('utm_medium', utmMedium);
            
            return url.toString();
        };
        
        return (
            <div 
                key={manifest.gameId}
                style={{
                    display: 'flex', 
                    flexDirection: 'row', 
                    alignItems: 'center', 
                    gap: '1.5rem',
                    cursor: "pointer",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    padding: "1.5rem",
                    borderRadius: "16px",
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    width: '100%',
                    maxWidth: '600px',
                }}
                onClick={() => window.open(buildGameUrlWithTracking(), '_blank')}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.3), 0 0 20px rgba(16, 185, 129, 0.2)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
                }}
            >
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    flexShrink: 0
                }}>
                    <img 
                        src={gameIconMap[manifest.gameId as GameId] ?? manifest.gameIcon ?? ''} 
                        alt={`${manifest.gameName} icon`}
                        style={{ 
                            height: "80px",
                            width: "80px",
                            borderRadius: "12px",
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                        }}
                    />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                        fontSize: "1.25rem", 
                        margin: "0 0 0.5rem 0", 
                        color: textColor, 
                        fontFamily: "Open Sans",
                        fontWeight: 700,
                        lineHeight: 1.2
                    }}>
                        {manifest.gameName}
                    </p>
                    <p style={{
                        fontSize:"0.95rem", 
                        margin: "0", 
                        color: textColor, 
                        fontFamily: "Open Sans",
                        fontStyle: showDescription ? "normal" : "italic",
                        opacity: showDescription ? 0.8 : 0.6,
                        lineHeight: 1.4
                    }}>
                        {displayText}
                    </p>
                </div>
            </div>
        );
    }

    const renderLandingPageLink = () => {
        return (
            <div style={{marginTop: '0.5rem'}}>
                {/* Centered link with icon */}
                <a 
                    href="http://mentalmintgames.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.75rem',
                        color: textColor,
                        textDecoration: 'none',
                        fontSize: '1.1rem',
                        fontFamily: 'Open Sans',
                        cursor: 'pointer',
                        padding: '1rem'
                    }}
                >
                    <img 
                        src={mentalMintLogo} 
                        alt="Mental Mint" 
                        style={{ 
                            height: '1.5rem',
                            width: '1.5rem',
                            objectFit: 'contain'
                        }}
                    />
                    <span style={{opacity: 0.6}}>More about Mental Mint</span>
                </a>
            </div>
        );
    }

    return (
        <div style={{
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: '1.5rem',
            width: '100%',
            padding: '1rem 0'
        }}>
            {/* Render games based on launch status, sorted by launch status */}
            {Object.values(GAME_METADATAS)
                .filter((manifest) => shouldShowInMoreGames(manifest.launchStatus))
                .sort((a, b) => getSortOrder(a.launchStatus) - getSortOrder(b.launchStatus))
                .map((manifest) => renderSingleGame(manifest))
            }
            
            {/* Render landing page link if requested */}
            {includeLandingPageLink && renderLandingPageLink()}
        </div>
    );
};

export default MoreGames;